import { zodResolver } from "@hookform/resolvers/zod"
import {
  type FieldValues,
  FormProvider,
  type Path,
  useForm,
  type UseFormRegister,
} from "react-hook-form"

import Button from "~/v2/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/v2/components/ui/card"
import VaasAiMetadataFields from "~/v2/components/vaas/vaas-ai-metadata-fields"
import VaasThumbnailField from "~/v2/components/vaas/vaas-thumbnail-field"
import { createVaasSchema } from "~/v2/lib/vaas-schema"
import type { VaasCreateValues } from "~/v2/types/vaas"

const defaultValues = {
  course_type: "vaas",
  disable_final_assessment: true,
  course_name: "",
  course_thumbnail: {},
  created_by: null,
  lesson_description: "",
  vaas_creation_timestamp: "",
  vaas_logo_redirect: "",
  vaas_provider_name: "",
  vaas_show_feedback: false,
  vaas_system_version: "",
}

type FieldProps<TFieldValues extends FieldValues = FieldValues> = {
  description?: string
  error?: unknown
  label: string
  name: Path<TFieldValues>
  register: UseFormRegister<TFieldValues>
  rows?: number
  type?: string
}

const Field = ({
  description,
  label,
  name,
  register,
  rows,
  type = "text",
  error,
}: FieldProps) => {
  const Component = rows ? "textarea" : "input"

  return (
    <label className="flex flex-col gap-2">
      <span className="text-sm font-medium text-foreground">{label}</span>
      {description && (
        <span className="text-xs text-muted-foreground">{description}</span>
      )}
      <Component
        {...register(name)}
        className="rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-primary"
        rows={rows}
        type={type}
      />
      {Boolean(error) && (
        <span className="text-xs text-destructive">{String(error)}</span>
      )}
    </label>
  )
}

type VaasCreateFormProps = {
  createError?: string
  isLoading?: boolean
  onCancel: () => void
  onSubmit: (values: VaasCreateValues) => Promise<void> | void
}

const VaasCreateForm = ({
  createError,
  isLoading,
  onSubmit,
  onCancel,
}: VaasCreateFormProps) => {
  const methods = useForm<any>({
    defaultValues,
    resolver: zodResolver(createVaasSchema) as any,
  })

  const {
    formState: { errors },
    handleSubmit,
    register,
  } = methods

  return (
    <FormProvider {...methods}>
      <form
        className="flex flex-col gap-6"
        onSubmit={handleSubmit(onSubmit as any)}
      >
        <input {...register("course_type")} type="hidden" value="vaas" />
        <input {...register("disable_final_assessment")} type="hidden" />

        <Card>
          <CardHeader>
            <CardTitle>Course details</CardTitle>
            <CardDescription>
              Basic VAAS course fields for first create pass.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-2">
            <Field
              error={errors.course_name?.message}
              label="Course name"
              name="course_name"
              register={register}
            />
            <div className="md:col-span-2">
              <Field
                description="Optional redirect URL for VAAS logo."
                error={errors.vaas_logo_redirect?.message}
                label="VAAS logo redirect"
                name="vaas_logo_redirect"
                register={register}
                type="url"
              />
            </div>
            <label className="flex items-start gap-3 rounded-md border border-border bg-muted/20 p-4 md:col-span-2">
              <input
                {...register("vaas_show_feedback")}
                className="mt-1"
                type="checkbox"
              />
              <span className="flex flex-col gap-1">
                <span className="text-sm font-medium text-foreground">
                  Show feedback
                </span>
                <span className="text-xs text-muted-foreground">
                  Allow VAAS learners to submit feedback in the app.
                </span>
              </span>
            </label>
            <VaasThumbnailField />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Lesson metadata</CardTitle>
            <CardDescription>
              AI-generated metadata for the VAAS lesson.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <VaasAiMetadataFields />
          </CardContent>
        </Card>

        {createError && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {createError}
          </div>
        )}

        <div className="flex items-center justify-end gap-3">
          <Button onClick={onCancel} type="button" variant="outline">
            Cancel
          </Button>
          <Button type="submit">
            {isLoading ? "Creating..." : "Create VAAS"}
          </Button>
        </div>
      </form>
    </FormProvider>
  )
}

export default VaasCreateForm
